import { computed, getCurrentInstance } from 'vue';
import { utcToZonedTime } from 'date-fns-tz';
import { isTimeAfter } from 'shared/helpers/DateHelper';
import { useNextAvailabilityTime } from './useNextAvailabilityTime';

/**
 * Composable for team availability status
 * Provides availability-related computed properties
 *
 * @param {Object} options - Options object
 * @param {import('vue').Ref<Array>} options.availableAgents - Ref to available agents array
 * @returns {Object} Object containing availability properties and methods
 */
export const useAvailability = ({ availableAgents } = {}) => {
  const vm = getCurrentInstance();
  const t = key => vm?.proxy?.$t(key) || key;
  const { timeLeftToBackInOnline, workingHours } = useNextAvailabilityTime();

  // Channel config
  const channelConfig = computed(() => window.chatwootWebChannel);

  // Methods
  const getDateWithOffset = utcOffset => {
    return utcToZonedTime(new Date().toISOString(), utcOffset);
  };

  // Availability computed properties
  const currentDayAvailability = computed(() => {
    const { utcOffset } = channelConfig.value;
    const dayOfTheWeek = getDateWithOffset(utcOffset).getDay();
    const [workingHourConfig = {}] = workingHours.value.filter(
      workingHour => workingHour.day_of_week === dayOfTheWeek
    );
    return {
      closedAllDay: workingHourConfig.closed_all_day,
      openHour: workingHourConfig.open_hour,
      openMinute: workingHourConfig.open_minutes,
      closeHour: workingHourConfig.close_hour,
      closeMinute: workingHourConfig.close_minutes,
      openAllDay: workingHourConfig.open_all_day,
    };
  });

  const isInBetweenTheWorkingHours = computed(() => {
    const {
      openHour,
      openMinute,
      closeHour,
      closeMinute,
      closedAllDay,
      openAllDay,
    } = currentDayAvailability.value;

    if (openAllDay) return true;
    if (closedAllDay) return false;

    const { utcOffset } = channelConfig.value;
    const today = getDateWithOffset(utcOffset);
    const currentHours = today.getHours();
    const currentMinutes = today.getMinutes();

    const isAfterStartTime = isTimeAfter(
      currentHours,
      currentMinutes,
      openHour,
      openMinute
    );
    const isBeforeEndTime = isTimeAfter(
      closeHour,
      closeMinute,
      currentHours,
      currentMinutes
    );
    return isAfterStartTime && isBeforeEndTime;
  });

  const noWorkingHoursSet = computed(() =>
    workingHours.value.every(slot => slot.closed_all_day === true)
  );

  const isOnline = computed(() => {
    const { workingHoursEnabled } = channelConfig.value;
    const anyAgentOnline = availableAgents?.value?.length > 0;

    if (workingHoursEnabled) {
      return isInBetweenTheWorkingHours.value;
    }
    return anyAgentOnline;
  });

  const isInBusinessHours = computed(() => {
    const { workingHoursEnabled } = channelConfig.value;
    return workingHoursEnabled ? isInBetweenTheWorkingHours.value : true;
  });

  // Reply time computed properties
  const replyTime = computed(() => channelConfig.value.replyTime);

  const replyTimeStatus = computed(() => {
    switch (replyTime.value) {
      case 'in_a_few_minutes':
        return t('REPLY_TIME.IN_A_FEW_MINUTES');
      case 'in_a_few_hours':
        return t('REPLY_TIME.IN_A_FEW_HOURS');
      case 'in_a_day':
        return t('REPLY_TIME.IN_A_DAY');
      default:
        return t('REPLY_TIME.IN_A_FEW_HOURS');
    }
  });

  const replyWaitMessage = computed(() => {
    const { workingHoursEnabled } = channelConfig.value;

    if (noWorkingHoursSet.value) {
      return t('TEAM_AVAILABILITY.OFFLINE');
    }

    if (workingHoursEnabled) {
      return isOnline.value
        ? replyTimeStatus.value
        : `${t('REPLY_TIME.BACK_IN')} ${timeLeftToBackInOnline.value}`;
    }
    return isOnline.value
      ? replyTimeStatus.value
      : t('TEAM_AVAILABILITY.OFFLINE');
  });

  const outOfOfficeMessage = computed(
    () => channelConfig.value.outOfOfficeMessage
  );

  return {
    // Computed
    channelConfig,
    currentDayAvailability,
    isInBetweenTheWorkingHours,
    noWorkingHoursSet,
    isOnline,
    isInBusinessHours,
    replyTime,
    replyTimeStatus,
    replyWaitMessage,
    outOfOfficeMessage,
    // Methods
    getDateWithOffset,
  };
};
